import React, { useEffect } from 'react'
import { makeStyles } from 'tss-react/mui'
import RecommendedProducts from './SearchResultsGrid/RecommendedProducts'
import { useSrpRecommendedProductsQuery } from '@src/queries/SrpRecommendedProductsQuery.generated'
import { sendEmptyRecommendationGA4Event } from '@src/utils/analytics'
import { useCurrentUser } from '@utils/useCurrentUser'
import { ErpType } from '@utils/useProductErpType'

const useStyles = makeStyles()((theme) => ({
  promo: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(6),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginTop: theme.spacing(6),
  },
  dark: {
    borderBottomColor: theme.palette.grey[600],
  },
  grey: {
    background: theme.palette.background.grey,
    borderColor: 'transparent',
    padding: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(0),
      paddingRight: theme.spacing(0),
    },
  },
}))

interface InjectPromoProductsProps {
  children: React.ReactNode[]
  searchKey: string
  variant?: 'light' | 'dark'
  calculateGenePosition?: (showRecommendedProducts: boolean) => void
}

const InjectPromoProducts: React.FC<InjectPromoProductsProps> = ({
  children,
  searchKey,
  variant = 'light',
  calculateGenePosition,
}) => {
  const { isBlueB2BUser } = useCurrentUser()

  const { data: recommendedProductsData } = useSrpRecommendedProductsQuery({
    variables: {
      input: {
        searchKey,
        ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
      },
    },
    ssr: false,
    onCompleted: (loadedData) => {
      if (
        !loadedData ||
        loadedData?.getSrpRecommendedProducts?.products.length === 0
      ) {
        sendEmptyRecommendationGA4Event({
          action: 'srp load',
          detail: searchKey.toLowerCase(),
          section: 'recommended products',
        })
      }
    },
  })

  const recommendedProducts =
    recommendedProductsData?.getSrpRecommendedProducts?.products

  const showRecommendedProducts =
    !!recommendedProducts && recommendedProducts.length > 0

  useEffect(() => {
    if (calculateGenePosition) {
      calculateGenePosition(showRecommendedProducts)
    }
  }, [calculateGenePosition])

  const { classes, cx } = useStyles()
  const childrenArray = React.Children.toArray(children)

  return (
    <>
      {childrenArray.slice(0, 2)}
      {showRecommendedProducts && (
        <div className={cx(classes.promo, variant === 'dark' && classes.grey)}>
          <RecommendedProducts products={recommendedProducts} />
        </div>
      )}
      {childrenArray.slice(2)}
    </>
  )
}

export default InjectPromoProducts
